// src/stores/authStore.js
import { defineStore } from "pinia";
import { ref, computed } from "vue";
import axios from "axios";
import AuthService from "@/services/authService";
import { toast } from "@/utils/toast";
import {
  setActiveCompanyId,
  clearActiveCompanyId,
} from "@/services/_headers.util";

import { useAccessStore } from "@/stores/accessStore";
import { useCatalogStore } from "@/stores/catalogStore";
import { useAppLoaderStore } from "@/stores/appLoaderStore";
import { useSessionLockStore } from "@/stores/sessionLockStore";
import {
  navigateToRegistrationRedirect,
  isTenantHost,
  isTenancyFrontendEnabled,
} from "@/utils/hostname";

// --- helpers -------------------------------------------------
function normalizeRoles(raw) {
  if (!Array.isArray(raw)) return [];
  return raw.map((r) => (typeof r === "string" ? r : r?.name)).filter(Boolean);
}

/**
 * Decide which company the user should be scoped to.
 * - Prefer the employee company (if employee profile exists)
 * - Else fallback to the first pivot company (seeded Admin case)
 */
function deriveActiveCompanyId(u) {
  if (!u) return null;

  const fromEmployee = u?.employee?.company_id ?? null;
  const ownerCompany = Array.isArray(u?.companies)
    ? u.companies.find((c) => c?.is_owner || c?.pivot?.is_owner)?.id ?? null
    : null;
  const fromPivot =
    Array.isArray(u?.companies) && u.companies.length
      ? u.companies[0]?.id ?? u.companies[0]
      : null;
  const fromActive = u?.active_company_id ?? null;

  if (isTenancyFrontendEnabled() && isTenantHost()) {
    return fromEmployee || ownerCompany || fromPivot || fromActive || null;
  }

  return fromActive || fromEmployee || ownerCompany || fromPivot || null;
}

/**
 * For non-SuperAdmins, ensure we always have an active company id.
 * For SuperAdmins, we leave the header unset (they should pick via switcher).
 */
function ensureActiveCompanyForUser(u, isSuperAdmin) {
  if (isSuperAdmin) {
    // Don’t auto-pick for SuperAdmin; clear header to avoid 403 confusion
    clearActiveCompanyId();
    return;
  }
  const cid = deriveActiveCompanyId(u);
  if (cid) setActiveCompanyId(cid);
  else clearActiveCompanyId();
}

// --- store ---------------------------------------------------
export const useAuthStore = defineStore(
  "auth",
  () => {
    // state
    const token = ref(localStorage.getItem("token") || null);
    const user = ref(JSON.parse(localStorage.getItem("user") || "null"));
    const loading = ref(false);
    const error = ref(null);

    // prime axios on refresh
    if (token.value) {
      axios.defaults.headers.common.Authorization = `Bearer ${token.value}`;
    }

    // getters
    const isAuthenticated = computed(() => !!token.value && !!user.value);
    const roles = computed(() => normalizeRoles(user.value?.roles));
    const isSuperAdmin = computed(() => roles.value.includes("SuperAdmin"));
    // const permissions = computed(() => user.value?.permissions || []);
    const permissions = computed(() => {
      const raw = user.value?.permissions || [];
      const catalog = useCatalogStore();

      // raw can be ["Client.Read"] OR [{name:"Client.Read"}]
      const list = raw
        .map((p) => (typeof p === "string" ? p : p?.name))
        .filter(Boolean);

      // normalize aliases like Clients.Read -> Client.Read
      return list.map((p) =>
        catalog.loaded ? catalog.normalizePermissionName(p) : p
      );
    });

    const can = computed(() => (perm) => permissions.value.includes(perm));
    const currentUser = computed(() => user.value);

    // internal: set/clear session consistently
    function setSession(newToken, newUser) {
      token.value = newToken || null;
      user.value = newUser
        ? { ...newUser, roles: normalizeRoles(newUser?.roles) }
        : null;

      if (newToken) {
        localStorage.setItem("token", newToken);
        axios.defaults.headers.common.Authorization = `Bearer ${newToken}`;
      } else {
        localStorage.removeItem("token");
        delete axios.defaults.headers.common.Authorization;
      }

      if (user.value) {
        localStorage.setItem("user", JSON.stringify(user.value));
      } else {
        localStorage.removeItem("user");
      }
    }

    function saveImpersonationBackup() {
      const backup = {
        token: token.value,
        user: user.value,
        activeCompanyId: localStorage.getItem("activeCompanyId") || null,
      };
      localStorage.setItem("impersonation_backup", JSON.stringify(backup));
    }

    function restoreImpersonationBackup() {
      const raw = localStorage.getItem("impersonation_backup");
      if (!raw) return false;
      const backup = JSON.parse(raw);

      setSession(backup.token, backup.user);

      // Restore active company header behavior
      if (backup.activeCompanyId)
        setActiveCompanyId(Number(backup.activeCompanyId));
      else clearActiveCompanyId();

      localStorage.removeItem("impersonation_backup");
      return true;
    }

    // actions
    const login = async (credentials) => {
      loading.value = true;
      error.value = null;
      useSessionLockStore().reset();

      try {
        // 1) basic login
        const res = await AuthService.login(credentials);
        const { token: tk, user: me } = res?.data || {};
        console.info(
          "[login] /login user raw:",
          JSON.parse(JSON.stringify(me))
        );
        if (!tk || !me) throw new Error("Malformed login response");

        setSession(tk, me);
        ensureActiveCompanyForUser(
          { ...me, roles: normalizeRoles(me?.roles) },
          normalizeRoles(me?.roles).includes("SuperAdmin")
        );
        // toast(`/login roles: ${JSON.stringify(normalizeRoles(me?.roles))}`);

        // 2) fetch freshest user payload from /user (unscoped)
        try {
          const meRes = await AuthService.getUser();
          if (meRes?.data) {
            //  toast(`/login roles: ${JSON.stringify(normalizeRoles(me?.roles))}`);

            setSession(tk, { ...user.value, ...meRes.data });
            console.info(
              "[login] after setSession (from /user) roles:",
              user.value?.roles
            );
          }
        } catch (userFetchError) {
          // ignore; we still have data from login
          console.warn("[login] /user fetch failed:", userFetchError?.message || userFetchError);
        }

        // 3) set active company for non-superadmins
        ensureActiveCompanyForUser(user.value, isSuperAdmin.value);

        toast("Logged in successfully");
        return res;
      } catch (err) {
        error.value =
          err?.response?.data?.message || err?.message || "Login failed";
        toast(error.value, "error");
        throw err;
      } finally {
        loading.value = false;
      }
    };

    // const register = async (payload) => {
    //   loading.value = true;
    //   error.value = null;
    //   try {
    //     const res = await AuthService.register(payload);
    //     const { token: tk, user: me } = res?.data || {};
    //     if (tk && me) setSession(tk, me);
    //     toast("Registered successfully");
    //     return res;
    //   } catch (err) {
    //     error.value = err?.response?.data?.message || "Registration failed";
    //     toast(error.value, "error");
    //     throw err;
    //   } finally {
    //     loading.value = false;
    //   }
    // };

    const register = async (payload) => {
      loading.value = true;
      error.value = null;
      useSessionLockStore().reset();

      try {
        const res = await AuthService.register(payload);
        let data = res?.data;

        if (typeof data === "string") {
          try {
            data = JSON.parse(data.trim());
          } catch {
            data = null;
          }
        }

        if (navigateToRegistrationRedirect(data)) {
          toast("Account created. Verify your email to continue.");
          return { redirected: true };
        }

        const { token: tk, user: me } = data || {};
        if (tk && me) {
          setSession(tk, me);
          ensureActiveCompanyForUser(
            { ...me, roles: normalizeRoles(me?.roles) },
            normalizeRoles(me?.roles).includes("SuperAdmin")
          );
        } else if (data?.requires_email_verification) {
          throw new Error(
            "Workspace created, but the onboarding link is missing. Please contact support."
          );
        }

        if (tk) {
          try {
            const meRes = await AuthService.getUser();
            if (meRes?.data) {
              setSession(tk, { ...user.value, ...meRes.data });
            }
          } catch (e) {
            console.warn("[register] /user fetch failed:", e?.message || e);
          }
        }

        ensureActiveCompanyForUser(user.value, isSuperAdmin.value);

        toast("Registered successfully");
        return { redirected: false, res };
      } catch (err) {
        error.value = err?.response?.data?.message || err?.message || "Registration failed";
        toast(error.value, "error");
        throw err;
      } finally {
        loading.value = false;
      }
    };

    const fetchCurrentUser = async () => {
      try {
        const res = await AuthService.getUser();
        if (res?.data) {
          setSession(token.value, { ...user.value, ...res.data });
          // re-ensure active company (useful if employee/company changed)
          ensureActiveCompanyForUser(user.value, isSuperAdmin.value);
        }
      } catch (err) {
        // If token expired etc., do nothing here (router guard can handle)
        console.error("fetchCurrentUser failed", err);
      }
    };

    const logout = () => {
      const access = useAccessStore();
      useSessionLockStore().reset();

      try {
        AuthService.logout();
      } catch {}

      setSession(null, null);
      clearActiveCompanyId();

      // ✅ important: clear plan/modules state so next user/session starts fresh
      access.resetAccess();

      toast("Logged out");
    };

    const impersonateCompany = async (companyId) => {
      if (!isSuperAdmin.value)
        throw new Error("Only SuperAdmin can impersonate.");

      const loader = useAppLoaderStore();
      loader.show();

      // save current superadmin session so we can come back
      saveImpersonationBackup();

      const res = await AuthService.impersonateCompany(companyId);

      if (res?.data?.redirect_url) {
        window.location.assign(res.data.redirect_url);
        return res;
      }

      const { token: tk, user: u } = res?.data || {};
      if (!tk || !u) throw new Error("Malformed impersonation response");

      setSession(tk, u);

      // Now you're acting as company user → set tenant header
      setActiveCompanyId(companyId);

      return res;
    };

    const stopImpersonation = () => {
      const loader = useAppLoaderStore();
      loader.show();

      const ok = restoreImpersonationBackup();
      if (!ok) {
        // fallback: if no backup, do a normal logout
        logout();
      }
    };

    return {
      // state
      token,
      user,
      loading,
      error,
      // getters
      isAuthenticated,
      currentUser,
      roles,
      isSuperAdmin,
      permissions,
      can,
      // actions
      setSession,
      login,
      register,
      logout,
      fetchCurrentUser,
      impersonateCompany,
      stopImpersonation,
    };
  },
  {
    persist: {
      pick: ["token", "user"],
    },
  }
);
